import { useEffect, useState } from "react";
import { mergePeople, previewMergePeople, type MergePlan } from "../actions";
import { IcMerge } from "./icons";
import { MergePlanView } from "./MergePlanView";
import { PersonPicker } from "./PersonPicker";

/**
 * Merge any other record into the one on screen, the way Google Contacts does.
 *
 * The Duplicates queue only ever offers records that share a phone or an email.
 * The same man entered once as "מנדי" with a mobile and once as "מנחם מענדל" with
 * a landline shares neither, and until now there was no way to put the two
 * together at all — the person screen could only point at the queue, which did
 * not contain them.
 *
 * The record on screen is the one kept: it is the one the reader chose to open,
 * and it wins every conflict. The other only fills gaps, and the server's plan is
 * shown before anything is written.
 */
export function MergeWithCard({
  personId,
  target,
  onTarget,
}: {
  personId: string;
  /** Lifted so the duplicate banner above can pre-fill it with one tap. */
  target: { id: string; name: string } | null;
  onTarget: (t: { id: string; name: string } | null) => void;
}) {
  const [plan, setPlan] = useState<MergePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // The preview follows the target, whoever set it — the picker here or a tap on
  // the duplicate banner above.
  const targetId = target?.id ?? null;
  useEffect(() => {
    setPlan(null);
    setErr(null);
    if (!targetId) return;
    setDone(null);
    if (targetId === personId) {
      setErr("זה אותו כרטיס. בחרו כרטיס אחר.");
      return;
    }
    let cancelled = false;
    setBusy(true);
    previewMergePeople(personId, targetId)
      .then((p) => !cancelled && setPlan(p))
      .catch((e) => !cancelled && setErr(e instanceof Error ? e.message : "לא הצלחנו לבדוק מה האיחוד יעשה. נסו שוב."))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [personId, targetId]);

  async function commit() {
    if (!plan) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await mergePeople(plan.keepId, plan.dropId);
      setDone(`איחדנו לכאן את „${r.dropName}”.`);
      setPlan(null);
      onTarget(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לאחד. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" id="merge-with">
      <div className="card-title">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcMerge size={16} /> איחוד עם איש קשר אחר
        </span>
      </div>
      <div className="card-pad">
        <p className="faint" style={{ marginBottom: 8 }}>
          הכרטיס הזה נשאר. מהכרטיס השני יעברו לכאן טלפונים, מייל, ציר הזמן, תרומות ותגיות, ואז הוא יימחק.
        </p>
        {!plan && (
          <PersonPicker
            personId={target?.id ?? null}
            personName={target?.name ?? ""}
            onPick={onTarget}
            disabled={busy}
            placeholder="חפשו את הכרטיס לאיחוד"
          />
        )}
        {busy && !plan && <p className="faint">בודקים…</p>}
        {!plan && err && <div className="notice error">{err}</div>}
        {done && <div className="notice info">{done}</div>}
      </div>
      {plan && (
        <MergePlanView
          plans={[plan]}
          busy={busy}
          err={err}
          onCommit={commit}
          onBack={() => {
            setPlan(null);
            onTarget(null);
          }}
        />
      )}
    </div>
  );
}
